import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordRequirements, passwordMeetsPolicy } from "../components/PasswordRequirements";
import { confirmPasswordReset, requestPasswordReset } from "../auth/session";

type Step = "request" | "confirm" | "done";

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("request");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const requestMutation = useMutation({
    mutationFn: () => requestPasswordReset(email.trim().toLowerCase()),
    onSuccess: () => {
      setError(null);
      setStep("confirm");
    },
    onError: (err) => setError(err instanceof Error ? err.message : t("common.error"))
  });

  const confirmMutation = useMutation({
    mutationFn: () => confirmPasswordReset({
      email: email.trim().toLowerCase(),
      code: code.trim(),
      newPassword
    }),
    onSuccess: () => {
      setError(null);
      setStep("done");
    },
    onError: (err) => setError(err instanceof Error ? err.message : t("common.error"))
  });

  const onSubmitRequest = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError(t("auth.forgot.errors.emailInvalid"));
      return;
    }
    requestMutation.mutate();
  };

  const onSubmitConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!code.trim()) {
      setError(t("auth.forgot.errors.codeRequired"));
      return;
    }
    if (!passwordMeetsPolicy(newPassword)) {
      setError(t("auth.forgot.errors.policyMismatch"));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t("auth.changePassword.mismatch"));
      return;
    }
    confirmMutation.mutate();
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-muted/30">
      <div className="w-full max-w-md bg-background rounded-lg border border-border p-6 space-y-4">
        <header>
          <h1 className="text-2xl font-bold">{t("auth.forgot.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("auth.forgot.subtitle")}</p>
        </header>

        {step === "request" && (
          <form onSubmit={onSubmitRequest} className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">{t("auth.forgot.emailLabel")}</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                placeholder="you@example.com"
                disabled={requestMutation.isPending}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" disabled={requestMutation.isPending || !email}>
              {requestMutation.isPending ? t("common.saving") : t("auth.forgot.sendCode")}
            </Button>
          </form>
        )}

        {step === "confirm" && (
          <form onSubmit={onSubmitConfirm} className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t("auth.forgot.codeSentTo", { email })}
            </p>
            <div className="space-y-1">
              <label className="text-sm font-medium">{t("auth.forgot.codeLabel")}</label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                placeholder="123456"
                disabled={confirmMutation.isPending}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">{t("auth.forgot.newPasswordLabel")}</label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                disabled={confirmMutation.isPending}
              />
              <PasswordRequirements password={newPassword} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">{t("auth.changePassword.confirmLabel")}</label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                disabled={confirmMutation.isPending}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={confirmMutation.isPending}>
                {confirmMutation.isPending ? t("common.saving") : t("auth.forgot.submit")}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={requestMutation.isPending}
                onClick={() => {
                  setError(null);
                  requestMutation.mutate();
                }}
              >
                {t("auth.forgot.resend")}
              </Button>
            </div>
          </form>
        )}

        {step === "done" && (
          <div className="space-y-3">
            <p className="text-sm text-green-700 dark:text-green-300">{t("auth.forgot.successMessage")}</p>
            <Button onClick={() => navigate("/login", { replace: true })}>
              {t("auth.forgot.backToLogin")}
            </Button>
          </div>
        )}

        <div className="pt-2 border-t border-border">
          <Link to="/login" className="text-sm text-primary hover:underline">
            ← {t("auth.forgot.backToLogin")}
          </Link>
        </div>
      </div>
    </div>
  );
}
